// src/pages/MSME/PRSHome.jsx
import React, { useState, useEffect } from "react";
import Papa from "papaparse";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faUpload,
  faRobot,
  faComments,
  faEnvelope,
  faPhone,
  faChartPie,
  faSearch,
  faTimes,
  faExclamationTriangle,
  faClock,
  faFileCsv,
  faDatabase,
  faSync,
  faCheckCircle
} from "@fortawesome/free-solid-svg-icons";
import {
  Chart as ChartJS,
  ArcElement,
  Tooltip,
  Legend,
  LineElement,
  CategoryScale,
  LinearScale,
  PointElement,
} from "chart.js";
import { Doughnut, Line } from "react-chartjs-2";

ChartJS.register(ArcElement, Tooltip, Legend, LineElement, CategoryScale, LinearScale, PointElement);

// Dummy sample data for Load Sample feature
const dummyData = [
  {
    "Customer Name": "Rajesh Enterprises",
    "Bill No": "INV-001",
    "Due Date": "2025-10-25",
    "Amount": 15000,
    "Contact Number": "9876543210",
    "Email": "rajesh@enterprises.com",
    "WhatsApp Number": "9876543210",
    "Message": "Dear Rajesh, your payment of ₹15,000 for INV-001 is overdue. Please settle at your earliest convenience. Thank you!"
  },
  {
    "Customer Name": "Priya Textiles",
    "Bill No": "INV-002",
    "Due Date": "2025-11-06",
    "Amount": 8500,
    "Contact Number": "8765432109",
    "Email": "priya@textiles.in",
    "WhatsApp Number": "8765432109",
    "Message": "Hi Priya, friendly reminder: ₹8,500 due on Nov 6 for INV-002. Looking forward to your prompt payment! 😊"
  },
  {
    "Customer Name": "Amit Auto Parts",
    "Bill No": "INV-003",
    "Due Date": "2025-11-03",
    "Amount": 22000,
    "Contact Number": "7654321098",
    "Email": "amit@autoparts.co",
    "WhatsApp Number": "7654321098",
    "Message": "Hello Amit, urgent: Overdue payment of ₹22,000 for INV-003. Kindly remit immediately to avoid late fees."
  },
  {
    "Customer Name": "Sita Garments",
    "Bill No": "INV-004",
    "Due Date": "2025-11-08",
    "Amount": 12000,
    "Contact Number": "6543210987",
    "Email": "sita@garments.com",
    "WhatsApp Number": "6543210987",
    "Message": "Dear Sita, your ₹12,000 invoice INV-004 is due on Nov 8. We appreciate your timely attention!"
  },
  {
    "Customer Name": "Vikram Machinery",
    "Bill No": "INV-005",
    "Due Date": "2025-11-02",
    "Amount": 9500,
    "Contact Number": "5432109876",
    "Email": "vikram@machinery.in",
    "WhatsApp Number": "5432109876",
    "Message": "Hi Vikram, payment for INV-005 (₹9,500) is past due since Nov 2. Please update us on status."
  },
  {
    "Customer Name": "Neha Supplies",
    "Bill No": "INV-006",
    "Due Date": "2025-11-12",
    "Amount": 18000,
    "Contact Number": "4321098765",
    "Email": "neha@supplies.co",
    "WhatsApp Number": "4321098765",
    "Message": "Reminder: Future due on Nov 12 for ₹18,000 (INV-006). No action needed yet, but mark your calendar!"
  },
  {
    "Customer Name": "Kiran Electronics",
    "Bill No": "INV-007",
    "Due Date": "2025-11-05",
    "Amount": 7500,
    "Contact Number": "3210987654",
    "Email": "kiran@electronics.com",
    "WhatsApp Number": "3210987654",
    "Message": "Hello Kiran, due tomorrow: ₹7,500 for INV-007. A quick payment would be great!"
  },
  {
    "Customer Name": "Rohit Hardware",
    "Bill No": "INV-008",
    "Due Date": "2025-10-30",
    "Amount": 11000,
    "Contact Number": "2109876543",
    "Email": "rohit@hardware.in",
    "WhatsApp Number": "2109876543",
    "Message": "Urgent overdue alert: ₹11,000 for INV-008 overdue by 5 days. Let's resolve this soon."
  }
];

const tallyDummyData = [
  {
    customer: "Shree Ganesh Traders",
    billNo: "TG-1023",
    dueDate: "2025-01-10",
    amount: 18450,
    contact: "9823012345",
  },
  {
    customer: "Mahadev Electricals",
    billNo: "ME-774",
    dueDate: "2025-01-15",
    amount: 32600,
    contact: "9876543210",
  },
];

const prsDummyData = [
  {
    customer: "Aarav Enterprises",
    billNo: "PRS-8891",
    dueDate: "2025-01-08",
    amount: 9500,
    contact: "9000011122",
  },
  {
    customer: "Sai Packaging",
    billNo: "PRS-8892",
    dueDate: "2025-01-12",
    amount: 14200,
    contact: "9112233445",
  },
];

export default function PRSHome() {
  const [data, setData] = useState([]);
  const [aiSummary, setAiSummary] = useState("");
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [showAIChat, setShowAIChat] = useState(false);
  const [aiMessages, setAiMessages] = useState([
    { from: "ai", text: "Hello! I am your Payment Reminder Assistant!" },
  ]);

  // Load Sample handler
  const loadSample = () => {
    setData(dummyData);
  };
  const [previewData, setPreviewData] = useState([]);
  const [source, setSource] = useState(null);
  const [imported, setImported] = useState(false);

  const syncWithTally = () => {
    setSource("Tally (CSV)");
    setPreviewData(tallyDummyData);
    setImported(false);
  };

  const syncWithPRS = () => {
    setSource("PRS DB Dump");
    setPreviewData(prsDummyData);
    setImported(false);
  };

  const confirmImport = () => {
    setImported(true);
  };

  // CSV upload handler
  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      complete: (results) => {
        setData(results.data);
      },
    });
  };

  // AI summary logic
  useEffect(() => {
    if (data.length > 0) {
      const total = data.reduce((sum, d) => sum + Number(d.Amount || 0), 0);
      const overdue = data.filter((d) => new Date(d["Due Date"]) < new Date());
      const dueSoon = data.filter((d) => {
        const diff = (new Date(d["Due Date"]) - new Date()) / (1000 * 60 * 60 * 24);
        return diff >= 0 && diff <= 5;
      });
      const avgDelay = overdue.length
        ? overdue.reduce(
            (sum, d) => sum + (new Date() - new Date(d["Due Date"])) / (1000 * 60 * 60 * 24),
            0
          ) / overdue.length
        : 0;

      setAiSummary(
        `📊 Total Dues ₹${total.toLocaleString()} | 🚨 Overdue: ${overdue.length} | ⏳ Due Soon: ${
          dueSoon.length
        }\n⏰ Avg Delay: ${avgDelay.toFixed(1)} days.\n🤖 Tip: ${
          overdue.length > 0
            ? "Focus on overdue accounts first!"
            : "Send gentle reminders for upcoming dues."
        }`
      );
    } else {
      setAiSummary("🤖 Upload a dues CSV or load sample data to get instant AI insights.");
    }
  }, [data]);

  // Filter logic
  const filteredData = data
    .filter((item) => {
      if (filter === "all") return true;
      const dueDate = new Date(item["Due Date"]);
      if (filter === "overdue") return dueDate < new Date();
      if (filter === "dueSoon") {
        const diff = (dueDate - new Date()) / (1000 * 60 * 60 * 24);
        return diff >= 0 && diff <= 5;
      }
      return true;
    })
    .filter(
      (row) =>
        row["Customer Name"]?.toLowerCase().includes(search.toLowerCase()) ||
        row["Bill No"]?.toLowerCase().includes(search.toLowerCase())
    );

  // Chart data simulation
  const chartData = {
    labels: ["Overdue", "Due Soon", "Future"],
    datasets: [
      {
        data: [
          data.filter((d) => new Date(d["Due Date"]) < new Date()).length,
          data.filter((d) => {
            const diff = (new Date(d["Due Date"]) - new Date()) / (1000 * 60 * 60 * 24);
            return diff >= 0 && diff <= 5;
          }).length,
          data.filter((d) => {
            const diff = (new Date(d["Due Date"]) - new Date()) / (1000 * 60 * 60 * 24);
            return diff > 5;
          }).length,
        ],
        backgroundColor: ["#f87171", "#14b8a6", "#10b981"],
      },
    ],
  };

  const lineData = {
    labels: ["Overdue", "Due Soon", "Future"],
    datasets: [
      {
        label: "Payments Status Count",
        data: chartData.datasets[0].data,
        borderColor: "#10b981",
        tension: 0.4,
        fill: false,
      },
    ],
  };

  // Dummy AI chat
  const handleUserQuery = (q) => {
    setAiMessages((prev) => [...prev, { from: "user", text: q }]);
    setTimeout(() => {
      let reply = "I’m analyzing that...";
      if (q.toLowerCase().includes("overdue"))
        reply = "You have a few overdue accounts. Prioritize high-value ones today.";
      else if (q.toLowerCase().includes("summary"))
        reply = aiSummary;
      else if (q.toLowerCase().includes("suggest"))
        reply = "Try sending personalized WhatsApp messages with emojis 😊 for better response!";
      else if (q.toLowerCase().includes("whatsapp"))
        reply = "I recommend starting with WhatsApp for quick, informal reminders. Use the pre-filled messages for personalization.";
      else if (q.toLowerCase().includes("email"))
        reply = "Emails are great for formal records. Attach the invoice PDF if available.";
      else
        reply = "I can help summarize dues, suggest reminders, or prioritize actions. Try asking 'Show summary' or 'Suggest for WhatsApp'.";
      setAiMessages((prev) => [...prev, { from: "ai", text: reply }]);
    }, 1000);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && e.target.value.trim()) {
      handleUserQuery(e.target.value);
      e.target.value = "";
    }
  };

  const Button = ({ children, variant = "default", size = "md", className = "", ...props }) => {
    const base = "font-medium rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2";
    const variants = {
      primary: "bg-emerald-600 hover:bg-emerald-700 text-white focus:ring-emerald-500",
      secondary: "bg-teal-600 hover:bg-teal-700 text-white focus:ring-teal-500",
      danger: "bg-rose-600 hover:bg-rose-700 text-white focus:ring-rose-500",
      outline: "bg-slate-700 hover:bg-slate-600 text-slate-300 border border-slate-600 focus:ring-slate-500",
      ghost: "bg-transparent hover:bg-slate-700 text-slate-300 focus:ring-slate-500",
    };
    const sizes = {
      lg: "px-3 py-1.5 text-xs",
      md: "px-4 py-2 text-sm",
      lg: "px-6 py-3 text-base",
    };
    return (
      <button
        className={`${base} ${variants[variant] || variants.primary} ${sizes[size] || sizes.md} ${className}`}
        {...props}
      >
        {children}
      </button>
    );
  };

  return (
    <div className="min-h-screen bg-slate-900 text-slate-100 [&_p]:text-gray-50 p-2 lg:p-4">
      <header className="text-center mb-4">
        <h1 className="text-lg lg:text-xl font-bold text-yellow-200 mb-1">💰 Payment Reminder System (PRS)</h1>
        <p className="text-xs text-slate-200">Think5Why AMI (Artificial MSME Intellegence) Hub: Smart AI for Payment Follow-up</p>
      </header>

      <section className="bg-slate-800 border border-slate-700 p-3 sm:p-4 rounded-xl mb-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <label
            htmlFor="csvUpload"
            className="flex items-center justify-center gap-2 h-11 rounded-lg bg-emerald-500 text-black text-sm font-medium cursor-pointer hover:bg-emerald-400 transition"
          >
            <FontAwesomeIcon icon={faUpload} />
            Upload Dues CSV
          </label>
          <input id="csvUpload" type="file" accept=".csv" className="hidden" />

          <Button
            onClick={syncWithPRS}
            variant="outline"
            className="h-11 w-full flex items-center justify-center gap-2 border-cyan-400 text-cyan-300 hover:bg-cyan-900"
          >
            <FontAwesomeIcon icon={faDatabase} />
            Sync With PRS DB Dump
          </Button>

          <Button
            onClick={syncWithTally}
            variant="outline"
            className="h-11 w-full flex items-center justify-center gap-2 border-indigo-400 text-indigo-300 hover:bg-indigo-900"
          >
            <FontAwesomeIcon icon={faFileCsv} />
            Sync With Tally (CSV)
          </Button>
        </div>
        <p className="mt-3 text-xs text-slate-300 text-center leading-relaxed">
          Supports fields: Customer Name, Bill No, Due Date, Amount, Contact Number, Email, WhatsApp Number, Message
        </p>

        {previewData.length > 0 && (
          <div className="mt-4 bg-slate-900 border border-slate-700 rounded-lg p-3">
            <div className="text-sm text-slate-300 mb-2">
              Preview from <span className="font-semibold">{source}</span>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-xs text-left text-slate-300">
                <thead className="text-slate-400 border-b border-slate-700">
                  <tr>
                    <th className="py-1">Customer</th>
                    <th>Bill</th>
                    <th>Due Date</th>
                    <th>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {previewData.map((row, i) => (
                    <tr key={i} className="border-b border-slate-800">
                      <td className="py-1">{row.customer}</td>
                      <td>{row.billNo}</td>
                      <td>{row.dueDate}</td>
                      <td>₹{row.amount.toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {!imported ? (
              <Button
                onClick={confirmImport}
                className="mt-3 w-full flex items-center justify-center gap-2"
              >
                <FontAwesomeIcon icon={faSync} />
                Confirm Import
              </Button>
            ) : (
              <div className="mt-3 flex items-center justify-center gap-2 text-emerald-400 text-sm">
                <FontAwesomeIcon icon={faCheckCircle} />
                {previewData.length} records imported successfully
              </div>
            )}
          </div>
        )}

        <p className="mt-3 text-xs text-slate-400 text-center">
          Supports CSV exports from Tally Outstanding Reports and internal PRS dumps.
        </p>
      </section>

      {/* AI Summary */}
      <section className="bg-gradient-to-r from-emerald-700 to-teal-600 rounded-xl p-3 lg:p-4 mb-4">
        <div className="flex items-center gap-2 mb-2">
          <FontAwesomeIcon icon={faRobot} className="text-sm" />
          <span className="font-semibold text-sm">AI Insights:</span>
        </div>
        <p className="text-sm whitespace-pre-line leading-relaxed">{aiSummary}</p>
      </section>

      {/* Charts */}
      {data.length > 0 && (
        <section className="bg-slate-800 border border-slate-700 rounded-xl p-3 lg:p-4 mb-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="flex flex-col items-center">
              <h3 className="text-sm mb-3 flex items-center gap-1 text-teal-300 font-medium">
                <FontAwesomeIcon icon={faChartPie} className="text-xs" /> Status Distribution
              </h3>
              <div className="w-full max-w-xs h-40 lg:h-48 flex items-center justify-center">
                <Doughnut data={chartData} options={{ maintainAspectRatio: false }} />
              </div>
            </div>
            <div className="flex flex-col items-center">
              <h3 className="text-sm mb-3 text-teal-300 font-medium">📈 Status Trend</h3>
              <div className="w-full max-w-xs h-40 lg:h-48 flex items-center justify-center">
                <Line data={lineData} options={{ maintainAspectRatio: false }} />
              </div>
            </div>
          </div>
        </section>
      )}

      {/* Search + Filter */}
      {data.length > 0 && (
        <section className="mb-4">
          <div className="flex flex-row items-stretch lg:items-center justify-between gap-1">
            <div className="flex flex-row items-center bg-slate-800 rounded-lg px-3 py-2 flex-1 max-w-md w-full">
              <FontAwesomeIcon icon={faSearch} className="text-slate-500 mr-2 text-sm" />
              <input
                type="text"
                placeholder="Search name or bill..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="bg-transparent outline-none text-sm flex-1"
              />
            </div>
            <div className="flex flex-row gap-1 text-xs justify-center justify-end">
              <Button
                onClick={() => setFilter("all")}
                variant={filter === "all" ? "primary" : "ghost"}
                size="sm"
                className="min-w-[2.5rem]"
              >
                All
              </Button>
              <Button
                onClick={() => setFilter("dueSoon")}
                variant={filter === "dueSoon" ? "secondary" : "ghost"}
                size="sm"
                className="min-w-[4rem] flex items-center gap-1 bg-yellow-600"
              >
                <FontAwesomeIcon icon={faClock} className="text-xs " /> Due Soon
              </Button>
              <Button
                onClick={() => setFilter("overdue")}
                variant={filter === "overdue" ? "danger" : "ghost"}
                size="sm"
                className="min-w-[4rem] flex items-center gap-1 bg-red-800"
              >
                <FontAwesomeIcon icon={faExclamationTriangle} className="text-xs " /> Overdue
              </Button>
            </div>
          </div>
        </section>
      )}

      {/* Data View */}
      <section className="space-y-3">
        {filteredData.length > 0 ? (
          filteredData.map((row, i) => {
            const isOverdue = new Date(row["Due Date"]) < new Date();
            const isDueSoon =
              (new Date(row["Due Date"]) - new Date()) / (1000 * 60 * 60 * 24) <= 5 && !isOverdue;
            const statusVariant = isOverdue ? "danger" : isDueSoon ? "secondary" : "primary";
            return (
              <article
                key={i}
                className={`p-3 lg:p-4 rounded-xl border ${
                  isOverdue
                    ? "border-rose-600 bg-rose-900/20"
                    : isDueSoon
                    ? "border-teal-600 bg-teal-900/20"
                    : "border-emerald-600 bg-emerald-900/20"
                }`}
              >
                <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-4">
                  <div className="flex-1">
                    <h3 className="font-semibold text-emerald-300 text-base lg:text-lg mb-1">
                      {row["Customer Name"] || "Unnamed"}
                    </h3>
                    <p className="text-xs text-slate-400 mb-2">
                      Bill <span className="font-mono">{row["Bill No"]}</span> | Due {new Date(row["Due Date"]).toLocaleDateString("en-IN")}
                    </p>
                    <p className="text-lg lg:text-xl font-bold text-emerald-400 mb-2">
                      ₹{Number(row["Amount"]).toLocaleString()}
                    </p>
                    <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${
                      isOverdue
                        ? "bg-rose-600/30 text-rose-300 border border-rose-500/50"
                        : isDueSoon
                        ? "bg-teal-600/30 text-teal-300 border border-teal-500/50"
                        : "bg-emerald-600/30 text-emerald-300 border border-emerald-500/50"
                    }`}>
                      {isOverdue
                        ? "⚠️ Overdue — urgent reminder suggested."
                        : isDueSoon
                        ? "⏳ Due soon — gentle message advised."
                        : "✅ Scheduled — wait to remind later."}
                    </span>
                    {row["Message"] && (
                      <p className="text-xs text-slate-300 mt-3 bg-slate-700/50 p-2 rounded border border-slate-600 leading-relaxed">
                        📝 {row["Message"].substring(0, 120)}...
                      </p>
                    )}
                  </div>
                  <div className="flex flex-row gap-2 w-full">
                    <Button
                      variant="primary"
                      size="sm"
                      className="flex-1 lg:flex-none text-xs w-20 lg:text-sm lg:w-32 justify-center"
                    >
                      <FontAwesomeIcon icon={faComments} className="mr-1 text-xs" />
                      WhatsApp
                    </Button>
                    <Button
                      variant="secondary"
                      size="sm"
                      className="flex-1 lg:flex-none text-xs w-20 lg:text-sm lg:w-32 justify-center"
                    >
                      <FontAwesomeIcon icon={faEnvelope} className="mr-1 text-xs" />
                      Email
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1 lg:flex-none text-xs w-20 lg:text-sm lg:w-32 justify-center"
                    >
                      <FontAwesomeIcon icon={faPhone} className="mr-1 text-xs" />
                      Call
                    </Button>
                  </div>
                </div>
              </article>
            );
          })
        ) : (
          <div className="text-center text-slate-500 py-12">
            <p className="text-sm">No data available. Try uploading a CSV or loading sample data.</p>
          </div>
        )}
      </section>

      {/* Floating AI Chat Agent */}
      <Button
        onClick={() => setShowAIChat(!showAIChat)}
        variant="primary"
        size="sm"
        className="fixed bottom-4 lg:bottom-5 right-4 lg:right-8 shadow-lg z-50 w-10 h-10 mt-4 lg:mt-8 p-0 flex items-center justify-center"
      >
        <FontAwesomeIcon icon={faRobot} className="text-sm" />
      </Button>

      {showAIChat && ( 
        <div className="fixed bottom-20 lg:bottom-24 right-4 lg:right-5 bg-slate-800 border border-slate-700 rounded-xl w-[calc(100vw-2rem)] lg:w-80 max-h-[50vh] lg:max-h-[70vh] flex flex-col shadow-2xl z-40">
          <header className="flex justify-between items-center p-3 border-b border-slate-700 bg-slate-900 rounded-t-xl">
            <h4 className="text-emerald-400 text-sm font-semibold flex items-center gap-2">
              🤖 PRS - AI Assistant
            </h4>
            <Button
              onClick={() => setShowAIChat(false)}
              variant="ghost"
              size="sm"
              className="p-1.5 text-slate-400 hover:text-slate-300 w-24"
            >
              <FontAwesomeIcon icon={faTimes} className="text-sm" />
            </Button>
          </header>
          <div className="flex-1 overflow-y-auto p-3 space-y-2 text-sm max-h-48">
            {aiMessages.map((msg, idx) => (
              <div
                key={idx}
                className={`p-2.5 rounded-lg max-w-[90%] ${
                  msg.from === "ai"
                    ? "bg-teal-700/30 self-start text-left border-l-4 border-teal-500"
                    : "bg-slate-700/50 self-end text-right border-r-4 border-slate-500"
                }`}
              >
                {msg.text}
              </div>
            ))}
          </div>
          <footer className="border-t border-slate-700 p-3">
            <input
              type="text"
              placeholder="Ask AI... (e.g., 'Suggest WhatsApp message')"
              onKeyDown={handleKeyDown}
              className="border border-slate-600 bg-slate-900 text-slate-100 text-sm p-2.5 outline-none w-full rounded focus:ring-2 focus:ring-emerald-500"
            />
          </footer>
        </div>
      )}
    </div>
  );
}